import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { TChannel, TCreativeType, TDisplayCreativeType, TMetaCreativeType } from '../types';

/** Step in the add creative flow */
export type TAddCreativeStep = 'channel' | 'creative-type';

/** Option configuration for creative type selection (compatible with Radios IRadioOption) */
export interface ICreativeTypeOption {
  /** Unique identifier for the option */
  id: TDisplayCreativeType | TMetaCreativeType;
  /** Display name */
  name: string;
  /** Description text */
  description: string;
}

/**
 * Return type for the useAddCreativeFlow hook.
 */
export interface IUseAddCreativeFlowResult {
  /** Current step in the flow */
  step: TAddCreativeStep;
  /** Currently selected channel */
  selectedChannel: TChannel | undefined;
  /** Currently selected creative type */
  selectedCreativeType: TCreativeType | undefined;
  /** Handler for channel selection */
  handleChannelSelect: (channel: TChannel) => void;
  /** Handler for creative type selection */
  handleCreativeTypeSelect: (creativeType: TCreativeType) => void;
  /** Handler for Next button on channel step */
  handleNextFromChannel: () => void;
  /** Handler for Next button on creative type step */
  handleNextFromCreativeType: () => void;
  /** Handler for Back button */
  handleBack: () => void;
  /** Handler for closing the modal */
  handleClose: () => void;
  /** Whether the Next button should be disabled on channel step */
  isNextDisabledOnChannel: boolean;
  /** Whether the Next button should be disabled on creative type step */
  isNextDisabledOnCreativeType: boolean;
  /** Modal title for current step */
  modalTitle: string;
  /** Modal description for current step */
  modalDescription: string;
  /** Creative type options based on selected channel */
  creativeTypeOptions: ICreativeTypeOption[];
}

/**
 * Custom hook that manages state and logic for the AddCreativeFlow component.
 *
 * Handles:
 * - Step navigation (channel -> creative-type)
 * - Channel and creative type selection state
 * - Modal title/description based on current step
 * - Back navigation (preserves channel, clears creative type)
 * - Close/reset behavior
 *
 * @param onClose - Callback when modal should close
 * @param onComplete - Callback when flow is completed with channel and creative type
 * @returns Object containing state, handlers, and computed values
 *
 * @example
 * const { step, handleChannelSelect, handleNextFromChannel } = useAddCreativeFlow({
 *   onClose: () => setIsOpen(false),
 *   onComplete: (channel, type) => console.log(channel, type),
 * });
 */
export function useAddCreativeFlow({
  onClose,
  onComplete,
  initialStep = 'channel',
  initialChannel,
}: {
  onClose: () => void;
  onComplete: (channel: TChannel, creativeType: TCreativeType) => void;
  initialStep?: TAddCreativeStep;
  initialChannel?: TChannel;
}): IUseAddCreativeFlowResult {
  const { t } = useTranslation();
  const [step, setStep] = useState<TAddCreativeStep>(initialStep);
  const [selectedChannel, setSelectedChannel] = useState<TChannel | undefined>(initialChannel);
  const [selectedCreativeType, setSelectedCreativeType] = useState<TCreativeType | undefined>();

  const resetState = useCallback((): void => {
    setStep('channel');
    setSelectedChannel(undefined);
    setSelectedCreativeType(undefined);
  }, []);

  const handleClose = useCallback((): void => {
    onClose();
    resetState();
  }, [onClose, resetState]);

  const handleBack = useCallback((): void => {
    setSelectedCreativeType(undefined);
    setStep('channel');
  }, []);

  const handleChannelSelect = useCallback((channel: TChannel): void => {
    setSelectedChannel(channel);
  }, []);

  const handleCreativeTypeSelect = useCallback((creativeType: TCreativeType): void => {
    setSelectedCreativeType(creativeType);
  }, []);

  const handleNextFromChannel = useCallback((): void => {
    if (selectedChannel) {
      setStep('creative-type');
    }
  }, [selectedChannel]);

  const handleNextFromCreativeType = useCallback((): void => {
    if (selectedChannel && selectedCreativeType) {
      onComplete(selectedChannel, selectedCreativeType);
      resetState();
    }
  }, [selectedChannel, selectedCreativeType, onComplete, resetState]);

  const getChannelTitle = useCallback(
    (channel: TChannel): string => {
      return channel === 'display' ? t('Display Ad') : t('Meta');
    },
    [t],
  );

  const displayCreativeTypeOptions: ICreativeTypeOption[] = [
    {
      id: 'image_video',
      name: t('Add Image/Video Creative'),
      description: t('Upload image or video files for your creative'),
    },
    {
      id: 'build',
      name: t('Build Creative'),
      description: t('Design a creative using the builder tool'),
    },
    {
      id: 'duplicate',
      name: t('Duplicate Existing Creative'),
      description: t('Copy an existing creative from your library'),
    },
    {
      id: 'ad_tag',
      name: t('Add Ad Tag Creative'),
      description: t('Use third-party ad tag HTML markup'),
    },
  ];

  const metaCreativeTypeOptions: ICreativeTypeOption[] = [
    {
      id: 'image_video',
      name: t('Add Image/Video Creative'),
      description: t('Upload image or video files for your creative'),
    },
    {
      id: 'build',
      name: t('Build Creative'),
      description: t('Design a creative using the builder tool'),
    },
    {
      id: 'duplicate',
      name: t('Duplicate Existing Creative'),
      description: t('Copy an existing creative from your library'),
    },
  ];

  const creativeTypeOptions =
    selectedChannel === 'display' ? displayCreativeTypeOptions : metaCreativeTypeOptions;

  const getModalTitle = (): string => {
    if (step === 'channel') {
      return t('Choose Channel');
    }
    if (selectedChannel) {
      return t('Choose {{channel}} Creative Type', { channel: getChannelTitle(selectedChannel) });
    }
    return t('Choose Creative Type');
  };

  const modalTitle = getModalTitle();

  const modalDescription =
    step === 'channel'
      ? t('Select the channel for your creative.')
      : t('Select the type of creative you want to create.');

  return {
    step,
    selectedChannel,
    selectedCreativeType,
    handleChannelSelect,
    handleCreativeTypeSelect,
    handleNextFromChannel,
    handleNextFromCreativeType,
    handleBack,
    handleClose,
    isNextDisabledOnChannel: !selectedChannel,
    isNextDisabledOnCreativeType: !selectedCreativeType,
    modalTitle,
    modalDescription,
    creativeTypeOptions,
  };
}
